import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { MapService } from '../leaflet/service/map.service';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private renderer: Renderer2;
    private currentTheme: 'light' | 'dark' | 'auto' = 'light';
    private mediaQueryList: MediaQueryList;

    constructor(
        rendererFactory: RendererFactory2,
        private mapService: MapService
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
        this.initTheme();
        this.setupListener();
    }

    private initTheme() {
        const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'auto';
        this.setTheme(savedTheme || 'light');
    }

    private setupListener() {
        this.mediaQueryList.addListener((e) => {
            if (this.currentTheme === 'auto') {
                this.applyTheme(e.matches ? 'dark' : 'light');
            }
        });
    }

    setTheme(theme: 'light' | 'dark' | 'auto') {
        this.currentTheme = theme;
        localStorage.setItem('theme', theme);

        if (theme === 'auto') {
            const prefersDark = this.mediaQueryList.matches;
            this.applyTheme(prefersDark ? 'dark' : 'light');
        } else {
            this.applyTheme(theme);
        }
    }

    private applyTheme(effectiveTheme: 'light' | 'dark') {
        if (effectiveTheme === 'dark') {
            this.renderer.addClass(document.body, 'dark-theme');
        } else {
            this.renderer.removeClass(document.body, 'dark-theme');
        }
        // The application theme must not alter the map tiles. Migrate the
        // legacy automatic dark-map selection back to a normal light layer.
        this.restoreNormalMapLayer();
    }

    private restoreNormalMapLayer() {
        // Only migrate the legacy dark basemap — do not override the user's chosen layer.
        const currentLayerName = localStorage.getItem('map');
        if (currentLayerName !== 'Dark Matter') {
            return;
        }

        const layerKey = 'OpenStreetMap';
        const map = this.mapService.map;
        const baseMaps = this.mapService.baseMaps;
        localStorage.setItem('map', layerKey);
        this.mapService.selectedMap = layerKey;

        if (map && baseMaps?.['Dark Matter'] && baseMaps?.[layerKey]) {
            map.addLayer(baseMaps[layerKey]);
            map.removeLayer(baseMaps['Dark Matter']);
        }

        this.mapService.setSelectedMapLayer(layerKey);
    }

    toggleTheme() {
        // Simple light <-> dark toggle (skip auto so one tap always changes appearance)
        const nextTheme: 'light' | 'dark' =
            this.currentTheme === 'dark' ||
            (this.currentTheme === 'auto' && this.mediaQueryList.matches)
                ? 'light'
                : 'dark';

        this.setTheme(nextTheme);
    }

    getTheme() {
        return this.currentTheme;
    }

    /** True when the UI is currently dark (including auto + system dark). */
    isDarkEffective(): boolean {
        if (this.currentTheme === 'auto') {
            return this.mediaQueryList.matches;
        }
        return this.currentTheme === 'dark';
    }
}
